import { motion } from "framer-motion";
import { Phone, Mail, MapPin, BadgeCheck, Copy } from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "./data";
import { LogoMark } from "./LogoMark";

const copy = (text, label) => {
  navigator.clipboard?.writeText(text);
  toast.success(`${label} copied to clipboard`);
};

export const Founder = () => {
  return (
    <section id="founder" data-testid="founder-section" className="relative py-28 px-5">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="relative overflow-hidden rounded-[2rem] glass p-8 md:p-14 grain"
        >
          <div className="absolute -top-32 right-0 w-96 h-96 rounded-full bg-purple-600/15 blur-[120px]" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-4 flex flex-col items-start">
              <div className="relative">
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-sky-500 to-purple-600 blur-2xl opacity-40" />
                <LogoMark variant="full" className="relative w-44 h-44 rounded-3xl ring-1 ring-indigo-400/30 p-2" />
              </div>
              <div className="mt-6 inline-flex items-center gap-2 font-mono text-xs text-emerald-500">
                <BadgeCheck className="w-4 h-4" /> Verified Canadian studio
              </div>
            </div>

            <div className="lg:col-span-8">
              <p className="font-mono text-xs tracking-[0.3em] uppercase overline mb-4">
                / The Studio
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight txt-primary leading-tight">
                A boutique app studio built on direct, honest engineering.
              </h2>
              <p className="mt-5 text-lg txt-soft leading-relaxed">
                Brainstem App Solutions is led by{" "}
                <span className="txt-primary font-semibold">{BRAND.founder}</span>,{" "}
                {BRAND.founderRole}. When you work with us, you work directly with
                the person building your product — no layers, no runaround. Just
                thoughtful software, delivered on time, from right here in Canada.
              </p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button
                  data-testid="founder-phone-link"
                  onClick={() => copy(BRAND.phone, "Phone")}
                  className="group flex items-center gap-3 p-4 rounded-2xl glass hover:border-purple-400/40 transition-colors text-left"
                >
                  <Phone className="w-5 h-5 text-indigo-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[11px] txt-dim">Direct line</p>
                    <p className="font-mono text-sm txt-primary truncate">{BRAND.phone}</p>
                  </div>
                  <Copy className="w-3.5 h-3.5 txt-dim group-hover:txt-primary ml-auto" />
                </button>
                <button
                  data-testid="founder-email-link"
                  onClick={() => copy(BRAND.email, "Email")}
                  className="group flex items-center gap-3 p-4 rounded-2xl glass hover:border-purple-400/40 transition-colors text-left"
                >
                  <Mail className="w-5 h-5 text-indigo-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[11px] txt-dim">Email</p>
                    <p className="font-mono text-xs txt-primary truncate">{BRAND.email}</p>
                  </div>
                  <Copy className="w-3.5 h-3.5 txt-dim group-hover:txt-primary ml-auto" />
                </button>
                <div className="flex items-center gap-3 p-4 rounded-2xl glass">
                  <MapPin className="w-5 h-5 text-indigo-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[11px] txt-dim">Based in</p>
                    <p className="font-mono text-sm txt-primary truncate">{BRAND.location}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
